import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { useAppBanner } from '@/contexts/appBanner';
import { useAppSelector } from '@/store/hooks';
import { CurrencyHud } from '@/components/hub/CurrencyHud';
import { LevelBadge } from '@/components/hub/LevelBadge';
import { UserHeaderBanner } from '@/components/banner/UserHeaderBanner';
import { BannerFigura } from '@/components/banner/BannerFigura';
import { useQlashUser } from '@/hooks/useQlashUser';

/**
 * La HUB COMPRESSA, in un punto solo: la (b) della voce di Luca L-2705 (02/10 16:31:19Z), scelta
 * DIVERSA dal consiglio del seggio, che era la (a). Testuale: «sulla Hub, mentre scorri, striscia
 * intera con le monete; al posto del nome il tasto della sezione, solo l'icona (44 px); il nome si
 * nasconde».
 * La striscia NON si stringe mai: la colonna (`setSideNode`, solo da compressa) sta DENTRO la
 * striscia, nel livello sopra lo stendardo, fra il nome e le monete. Quando la colonna ha un
 * contenuto (oggi il tasto della sezione, sulla Hub: solo icona, 44×44, `HubSections`) il nome si
 * nasconde con questa classe, sul LIVELLO: `:has` sul figlio colonna, e la regola colpisce il figlio
 * `[data-nome]`. La stessa famiglia di condizione che tma-orcb aveva chiesto per la (a) (iOS 15.4+).
 * Altrove la colonna e' vuota, e il nome resta.
 * ⚠️ Non sul nome con `:has(+ [data-colonna])`: in WebKit 26.5 quella forma (soggetto che guarda il
 * fratello SUCCESSIVO) combacia in `matches()` e in `querySelectorAll`, ma lo stile non si applica e
 * il nome restava visibile (misurato il 02/10 ~16:40Z). La forma genitore-figlio si applica.
 * ⚠️ Senza `:has` (Chrome/WebView < 105, iOS < 15.4) il nome NON si nasconde: nome e tasto insieme,
 * col nome ancora piu' troncato (degrado misurato nella PR #555).
 * La (a), che stringeva la striscia al 62% e nascondeva le monete, non c'e' piu'. ⚠️ Se la scelta
 * cambia, cambia insieme `MONETE_CON_LA_COLONNA_PIENA` in `tools/tma-e2e/scenarios/misura-testata.mjs`.
 */
const NOME_VIA_SE_LA_COLONNA_HA_UN_CONTENUTO = '[&:has(>[data-colonna]:not(:empty))>[data-nome]]:hidden';

/**
 * The app's constant chrome: the player's banner + avatar + name, same place on
 * every screen, above <main>. Tapping the banner navigates to /profile — banner
 * customization itself now lives in Profile → Settings → Appearance.
 *
 * Two slots let a screen speak through it without owning it: the top row for
 * options/menus/title, and an overlay layer over the banner box itself. See
 * `contexts/appBanner.ts`.
 */
export function AppBanner() {
  const { setTopNode, setOverlayNode, setSideNode, setBottomNode } = useAppBanner();
  // Only needed to resolve the SELECTED BANNER (IAP record) — not to print a
  // name. The banner shows the player's picture and their banner, nothing else.
  const { t } = useTranslation('general');
  // Una fonte sola per build: su una build Limulo Laravel non viene interrogato affatto.
  // Il perche' (e la corsa che c'era prima) in `hooks/useQlashUser.ts`.
  const me = useQlashUser();
  const compact = useAppSelector((s) => s.settings.bannerCompact);
  const navigate = useNavigate();

  /**
   * Sul Profilo il banner si stringe un po' per far posto al tasto di modifica.
   *
   * È una riduzione LIEVE (90%), diversa da quella dello scroll (62%): lì serve
   * a recuperare altezza, qui solo a liberare la colonna per un tondo.
   * ~~Passando da un'altra schermata la larghezza si anima~~: non piu' dal
   * 02/10 (layout-tma-orcb). La striscia e il Profilo sono due ALBERI diversi
   * (il `return` qui sotto), quindi entrando o uscendo dal Profilo stendardo e
   * avatar si rimontano: niente animazione della larghezza, e il video
   * dell'avatar riparte da capo. [Misurato il 02/10 ~14:03Z, rete finta e una
   * clip finta: dopo Hub → Profilo e dopo Profilo → Hub il `<video>` della
   * testata e' un elemento NUOVO; su main restava lo stesso. Nessuna chiamata
   * di rete in piu' (tma-orcb, revisione di #555).]
   *
   * Lo stato vive nella rotta e non in Redux: è una proprietà di DOVE SI È, non
   * una preferenza, e tenerlo in Redux avrebbe richiesto a ogni schermata di
   * ricordarsi di azzerarlo uscendo.
   */
  const onProfile = useLocation().pathname === '/profile';

  if (!onProfile) {
    return (
      // LA STRISCIA — la testata su ogni schermata con la chrome, tranne il Profilo
      // (layout-tma-orcb, I-303/I-306/I-307; reference KB
      // `services/qlash-telegram-app/ui/2026-10-02-testata-striscia-reference.md`).
      // Prima era una riga in alto (livello e monete, 60-68 px anche VUOTA su staging)
      // sopra uno stendardo 395:99 con l'avatar che sporgeva sotto e copriva il titolo
      // della schermata: 180-198 px, il 22-29% del primo schermo. Ora una riga sola,
      // alta 56 px, con dentro tutto quello che c'era: figura, livello, nome, monete.
      // `pt-4`: e' il posto in cui la figura (72 px) sporge sopra la striscia (56). Sporge
      // SOLO verso l'alto: sotto, nel contenuto, mai.
      // ⛔ Niente riga in alto, quindi niente `topNode`: oggi nessuna rotta ci scrive
      // (`BannerTopSlot` non ha chiamanti), e uno slot montato ma nascosto inghiottirebbe
      // in silenzio la prima che lo fa. Fuori dal Profilo `BannerTopSlot` non disegna.
      <div className="relative z-10 shrink-0 px-4 pb-2 pt-4">
        {/* Lo stendardo ENTRA da fuori a sinistra (`-ml-8`) e ha la punta a destra,
            come prima (vedi sotto, ramo del Profilo).
            La COMPRESSIONE allo scroll non cambia ne' l'altezza ne' la larghezza: la
            striscia resta intera, con le monete, su ogni schermata. Sulla Hub il tasto
            della sezione entra AL POSTO DEL NOME (L-2705 (b), in cima al file). */}
        <div className="relative -ml-8 transition-all duration-300 ease-out">
          <UserHeaderBanner
            userId={me?.id}
            onPress={() => navigate('/profile')}
            ariaLabel={t('profile', { defaultValue: 'Profile' })}
            altezzaPx={56}
            // `block`: un `<button>` e' inline-block, e la sua riga aggiungeva 6 px di
            // discendente SOTTO lo stendardo. Il contenitore era alto 62 e non 56, e la
            // figura (ancorata al fondo del contenitore) finiva 6 px sotto il bordo della
            // striscia invece di poggiarci: testata 86 px invece di 80.
            className="block"
          />

          {/* Nome, colonna e monete stanno SOPRA lo stendardo, non dentro: lo stendardo e'
              un `<button>` (apre il Profilo), e il tasto della sezione e le monete hanno il
              loro. Un bottone dentro un bottone e' HTML non valido, e il tocco avrebbe aperto
              anche il Profilo. Il livello e' trasparente al tocco (sul nome si tocca lo
              stendardo); colonna e monete lo riprendono (`pointer-events-auto`).
              `pl-[92px]`: dopo la figura (14 + 72 px). `pr-[9%]`: il lato destro rientra
              nella punta (`UserBgShape`).
              Il nome si tronca con l'ellissi: a 360 px con le monete restano ~9-10
              caratteri. L'ombra serve perche' sotto puo' esserci una FOTO del giocatore. */}
          <div
            className={cn(
              'pointer-events-none absolute inset-0 z-[2] flex items-center gap-2 pl-[92px] pr-[9%]',
              NOME_VIA_SE_LA_COLONNA_HA_UN_CONTENUTO,
            )}
          >
            {me?.username && (
              <span
                data-nome
                className="min-w-0 truncate text-sm font-bold text-fg [text-shadow:0_1px_3px_rgba(0,0,0,0.85)]"
              >
                {me.username}
              </span>
            )}
            {/* La colonna: esiste solo da compressa, e conta solo se una rotta ci scrive
                (`empty:hidden`). Sta DOPO il nome, al suo posto quando lui si nasconde. */}
            {compact && (
              <div
                ref={setSideNode}
                data-colonna
                className="pointer-events-auto flex shrink-0 items-center empty:hidden"
              />
            )}
            {/* `data-tour="currency-hud"` e' dentro `CurrencyHud`: il tour della Hub
                si aggancia li', ovunque stia. */}
            <CurrencyHud stretta className="pointer-events-auto ml-auto w-auto shrink-0" />
          </div>

          {/* The player's QAvatar — la figura intera, 72 px, appoggiata al fondo. */}
          <BannerFigura striscia />

          {/* Il livello e' un bollino sull'angolo in basso a destra della figura: la
              stella intera (40 px) al 55%. */}
          <LevelBadge className="pointer-events-none absolute bottom-[-6px] left-[64px] z-[2] origin-bottom-left scale-[0.55]" />

          {/* Route overlay target. Sits last so it stacks above, and is
              click-through so the banner keeps its own tap. */}
          <div ref={setOverlayNode} className="pointer-events-none absolute inset-0 z-[1]" />
        </div>

        {/* La riga sotto la striscia. Oggi la usa solo il Profilo; resta per chi
            vorra' dire qualcosa sotto la testata. Niente `mt-6`: la figura non
            sporge piu' sotto. */}
        <div ref={setBottomNode} className="mt-3 empty:mt-0" />
      </div>
    );
  }

  // Da qui in giu': il PROFILO, invariato (ADR-0069, la targa di cuoio). Le condizioni
  // su `onProfile` che restano sotto vengono da quando questo ramo serviva ogni schermata.
  // La colonna a destra del banner esiste in due casi: da compressi ospita il
  // tasto della sezione (Hub), sul Profilo il tasto di modifica del banner.
  const hasSide = compact || onProfile;

  return (
    // relative z-10: the avatar is allowed to spill outside this box — it hangs
    // below the banner's bottom edge — and without a stacking order of its own
    // it would be painted over by <main>'s content.
    // pb-2: il banner finiva a 6px dal bordo della chrome mentre sopra ne aveva
    // 16 — appoggiato in basso invece che centrato nel suo spazio.
    // `pack-scope` su TUTTA la chrome del Profilo, non solo sulla riga in alto:
    // il contorno del banner legge `--color-brand` (vedi `UserBgShape`), quindi
    // restava l'ultimo pezzo di verde acqua su una schermata di cuoio.
    // Sul Profilo la chrome non poggia più direttamente sulle scaglie: diventa
    // una TARGA di cuoio, angoli inferiori stondati come la barra in basso. Le
    // due si guardano da capo a piede dello schermo e chiudono la schermata fra
    // due pezzi dello stesso materiale — in mezzo resta lo sfondo, che così
    // legge come il dentro dello zaino invece che come il fondo di tutto.
    //
    // La cucitura è la stessa della barra: un anello interno, non un `border`,
    // così non aggiunge un pixel all'ingombro né taglia gli angoli stondati.
    <div
      className={cn(
        'relative z-10 shrink-0 px-4 pt-3 transition-[padding] duration-300 ease-out',
        onProfile
          ? 'rounded-b-2xl bg-pack pb-4 pack-scope [box-shadow:inset_0_0_0_1px_var(--color-pack-stitch)]'
          : 'pb-2',
      )}
    >
      {/* La riga in alto (le valute) resta SEMPRE a tutta larghezza, estesa o
          compressa. Prima, da compressi, diventava la colonna a destra del
          banner: il portafoglio si rimpiccioliva e si spostava proprio mentre
          l'occhio stava seguendo lo scroll. Ora quella colonna ha un canale suo
          (`sideNode`) e ospita il tasto della sezione attiva. */}
      {/* Livello + valute sono CHROME, non contenuto della Hub: stanno qui e
          quindi compaiono su ogni schermata. Prima li montava la Hub attraverso
          il portale, e sparivano appena si entrava in una sottosezione — il
          saldo si vedeva solo dove non serviva spenderlo. */}
      <div className="relative z-[2] flex min-h-8 items-center gap-1.5">
        {/* Livello e nickname SPARISCONO sul Profilo, e le valute si prendono
            la riga intera.
            Non è pulizia: sul Profilo quelle due cose ci sono già, più grandi e
            al posto giusto — il nickname sotto il banner, il livello alle
            estremità della barra dell'esperienza. Ripeterle in alto le avrebbe
            mostrate due volte a due dimensioni diverse nella stessa schermata.

            SI RESTRINGE, non si smonta: la larghezza massima va a zero e il
            contenuto svanisce insieme, così arrivando da un'altra sezione le
            valute si allargano invece di saltare. Smontandolo il salto sarebbe
            stato secco, e chi arriva qui avrebbe visto la riga «rompersi».
            `-mr-1.5` mangia il proprio `gap` mentre chiude, altrimenti a
            larghezza zero resterebbe comunque un vuoto di sei pixel. */}
        <div
          aria-hidden={onProfile}
          className={cn(
            'flex min-w-0 items-center gap-1.5 overflow-hidden',
            'transition-[max-width,opacity,margin] duration-300 ease-out',
            onProfile ? 'pointer-events-none -mr-1.5 max-w-0 opacity-0' : 'max-w-[70%] opacity-100',
          )}
        >
          <LevelBadge />

          {/* Il nickname NON sta più qui: è DENTRO il banner, a destra e centrato
              in altezza (vedi sotto, `<UserHeaderBanner>`). Richiesta di Luca del
              2026-09-07 — sopra il banner occupava una riga sua.
              ⚠️ Misurato prima di spostarlo, e va detto perché cambia cosa ci si
              può aspettare: **togliere il nome da qui non recupera un pixel**. La
              riga ha `min-h-8` e la tengono su il livello e le valute comunque —
              32 px con la sessione senza portafoglio, 40 con la stella. Lo
              spostamento è un fatto di COMPOSIZIONE (il nome sul proprio
              stendardo), non di altezza: l'altezza si recupera solo toccando
              questa riga, che è una decisione sua e non è stata presa qui. */}
        </div>

        <CurrencyHud className="min-w-0 flex-1" />
        {/* Lo slot resta per una rotta che voglia dire la sua in questa riga. */}
        <div ref={setTopNode} className="flex shrink-0 items-center" />
      </div>

      {/* Il margine superiore sta sulla RIGA, non sul banner: applicato al
          banner entrava nel suo box e `items-center` centrava quel box
          margine incluso, spingendo il banner in basso e lasciando il tasto
          di sezione piu' in alto. Sulla riga, i due partono dalla stessa
          quota e si centrano fra loro. */}
      <div className={cn(hasSide ? 'mt-3 flex items-center gap-2' : 'contents')}>
        {/* Il banner ESCE dal bordo sinistro: -ml-8 supera il px-4 del
            contenitore, quindi il suo lato sinistro finisce fuori schermo e il
            bordo verde acqua non si vede. Cosi' lo stendardo sembra entrare da
            fuori invece di essere una scheda appoggiata al centro — e la punta
            a destra resta l'unico terminale visibile, che e' il verso giusto
            per una forma a bandiera. Lo shell ha overflow-hidden, quindi la
            parte in eccesso e' tagliata, non genera scorrimento. */}
        <div
          className={cn(
            // Da compressi il margine e' della riga (sopra), non di questo
            // elemento: qui spingerebbe giu' solo il banner.
            'relative -ml-8 transition-all duration-300 ease-out',
            compact ? 'w-[62%] shrink-0' : onProfile ? 'w-[90%] shrink-0' : 'mt-4',
          )}
        >
          {/* Il nome del giocatore sta sul suo stendardo, non sopra: `children`
              di `<UserHeaderBanner>` finisce in un livello `inset-0 items-center`,
              quindi il centraggio verticale è quello del banner e non un margine
              da tenere allineato a mano.
              `ml-auto` lo porta a destra; `max-w-[45%]` lo tiene fuori dalla metà
              sinistra, dove sporge l'avatar (`BannerAvatar`, `w-[46%]`).
              `pr-[15%]` e non un valore in pixel: il lato destro del banner NON è
              dritto, rientra in un galone (`UserBgShape`, il tracciato piega a
              ~7,4% della larghezza a mezza altezza). Una misura in percentuale
              resta dentro la punta anche da compressi, dove il banner vale il 62%.
              L'ombra del testo serve perché sotto può esserci una FOTO scelta dal
              giocatore: il velo nero del 25% da solo non garantisce il contrasto.
              ⛔ Sul Profilo no: lì il nickname c'è già, più grande, sotto il
              banner — è la stessa ragione per cui la riga in alto si chiude. */}
          <UserHeaderBanner
            userId={me?.id}
            onPress={() => navigate('/profile')}
            ariaLabel={t('profile', { defaultValue: 'Profile' })}
          >
            {!onProfile && me?.username && (
              <span className="ml-auto max-w-[45%] truncate pr-[15%] text-sm font-bold text-fg [text-shadow:0_1px_3px_rgba(0,0,0,0.85)]">
                {me.username}
              </span>
            )}
          </UserHeaderBanner>

          {/* The player's QAvatar — their official picture, every screen. */}
          <BannerFigura />

          {/* Route overlay target. Sits last so it stacks above, and is
              click-through so the banner keeps its own tap. */}
          <div ref={setOverlayNode} className="pointer-events-none absolute inset-0 z-[1]" />
        </div>

        {/* La colonna a destra del banner. Da compressi ospita il tasto della
            sezione attiva (Hub); sul Profilo il tasto di modifica del banner.
            A banner esteso e fuori dal Profilo non esiste: la griglia sarebbe
            visibile e direbbe la stessa cosa due volte. */}
        {hasSide && (
          <div ref={setSideNode} className="flex min-w-0 flex-1 items-center justify-end" />
        )}
      </div>

      {/* La riga sotto il banner: l'identità del giocatore (nickname + barra
          dell'esperienza sul Profilo).
          `mt-6` e non `mt-3`: l'avatar del giocatore SPORGE sotto il bordo
          inferiore del banner (vedi `BannerAvatar`, `bottom-[-25%]`), e con lo
          stacco normale la testa gli sarebbe finita addosso. */}
      <div ref={setBottomNode} className="mt-6 empty:mt-0" />

      {/* Il tasto per comprimere/espandere non c'e' piu': la compressione e'
          una CONSEGUENZA dello scroll, non una preferenza. Un comando manuale
          che fa la stessa cosa del gesto aggiunge un controllo da capire e uno
          stato che puo' contraddirlo (banner espanso a mano mentre si scorre). */}
    </div>
  );
}
